import classNames from "classnames";
import React, { forwardRef, useState } from "react";
import { KuikaButtonStatusContent, type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "./kuika-button-status";
import "./kuika-button.scss";

export {
  KUIKA_BUTTON_ERROR_HOLD_MS,
  KUIKA_BUTTON_SUCCESS_HOLD_MS,
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "./kuika-button-status";

export type KuikaButtonSize = "sm" | "md" | "lg";

/**
 * Glyph size per rung, in px — the --control-icon-* values. KuikaSvg sizes itself from props, so a
 * glyph placed inside a button of a given size takes it from here.
 */
export const KUIKA_BUTTON_ICON_SIZES: Record<KuikaButtonSize, number> = { sm: 14, md: 16, lg: 18 };

/**
 * The button also takes the standard button attributes it does not name itself — the event
 * handlers, `aria-*` and `data-*` that a wrapper like `KuikaTooltip`'s Radix trigger hands to the
 * element it anchors to. Without them that wrapper's props are a type error at the call site and
 * are dropped at runtime, and the tooltip never opens. `type`, `color` and `onClick` are the
 * button's own, spelled below.
 */
export interface KuikaButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "color" | "onClick" | "children"
> {
  type?: "primary" | "secondary" | "tertiary" | "text";
  htmlType?: "button" | "submit";
  size?: KuikaButtonSize;
  uppercase?: boolean;
  disabled?: boolean;
  active?: boolean;
  style?: React.CSSProperties;
  onClick?: (e?: any) => void;
  children: React.ReactNode;
  /**
   * The legacy busy state: a spinner is put before the label and the button is natively disabled.
   * Ignored when `status` is given.
   */
  loading?: boolean;
  /**
   * Where the button's action stands — the stateful save. `loading`, `success` and `error` roll the
   * label to their own ("Saving", "Saved", "Try again"; see `statusLabels`) and the button's width
   * follows it. While `loading` a click does nothing (a submit button does not submit) but the button
   * keeps its look and its focus; `error` is clickable, for the retry. The button is controlled:
   * the caller moves `status` back to `idle` (see KUIKA_BUTTON_SUCCESS_HOLD_MS /
   * KUIKA_BUTTON_ERROR_HOLD_MS). Not for icon buttons. Omit it and `loading` works exactly as before.
   */
  status?: KuikaButtonStatus;
  statusLabels?: IKuikaButtonStatusLabels;
  color?: "default" | "neutral" | "danger";
  /**
   * An icon-only square button. Without `size` it is the compact `sm` square (24px, radius 6); with
   * one it is that rung's square (md 32, lg 40), so the ladder — not a container override — decides.
   */
  isIcon?: boolean;
  /**
   * A permanent tinted fill using `--accent-tint`/`--accent-fg`, for a `tertiary` button that must
   * read as "on" at rest without being a toggle (a panel section's "+" add control). Distinct from
   * `active`: `active` is a transient pressed/selected state painted with `--blue-1`, which the dark
   * theme deliberately does NOT reuse as its tint (`--accent-tint` is its own, weaker mix there) —
   * so `active` alone is not token-correct for a resting affordance.
   */
  tinted?: boolean;
  className?: string;
  withIconButton?: React.ReactNode;
  isAIButton?: boolean;
  tabIndex?: number;
  id?: string;
  onDragStart?: React.DragEventHandler<HTMLElement>;
  // Associates a submit button with a <form> elsewhere in the DOM (HTML `form` attribute),
  // so a footer/toolbar button can submit a form it is not nested inside.
  form?: string;
  "data-testid"?: string;
  // Accessible name for icon-only buttons (no visible text child); forwarded to the <button>.
  "aria-label"?: string;
  /**
   * Disabled, but still focusable and pressable: a press does not run `onClick` (nor submit a
   * form) and the button shakes once, so the refusal is felt. Prefer it over `disabled` when the
   * button explains itself (a tooltip saying why); `disabled` stays for a button that is simply off.
   */
}

const KuikaButton = forwardRef<HTMLButtonElement, KuikaButtonProps>(
  (
    {
      type = "primary",
      htmlType = "button",
      size: propSize,
      uppercase = false,
      disabled = false,
      active = false,
      style,
      onClick,
      children,
      loading = false,
      status,
      statusLabels,
      color = "default",
      isIcon = false,
      tinted = false,
      onMouseEnter,
      onMouseLeave,
      className,
      withIconButton = null,
      isAIButton = false,
      tabIndex,
      id,
      "aria-disabled": ariaDisabled = false,
      ...rest
    },
    ref
  ) => {
    // An icon-only button with no size of its own is the compact square (24px); one that is GIVEN a
    // size draws it, so a 32px or 40px icon button no longer needs a container rule to grow it back.
    const size: KuikaButtonSize = propSize ?? (isIcon ? "sm" : "md");
    const hasStatus: boolean = status !== undefined;
    // `loading` is the legacy busy flag; a `status` replaces it.
    const isLegacyLoading: boolean = !hasStatus && loading;
    const isBusy: boolean = hasStatus ? status === "loading" : loading;
    const [isDenied, setIsDenied] = useState<boolean>(false);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
      if (ariaDisabled) {
        // Also keeps a submit button from submitting.
        event.preventDefault();
        setIsDenied(true);
        return;
      }

      if (isBusy || disabled) {
        event.preventDefault();
        return;
      }

      onClick?.(event);
    };

    const handleAnimationEnd = (event: React.AnimationEvent<HTMLButtonElement>): void => {
      // Animations inside the label bubble here too; only the button's own shake ends the refusal.
      if (event.target === event.currentTarget) {
        setIsDenied(false);
      }
    };

    const renderLoadingSpinner = () => isLegacyLoading && <span className="kuika-button__spinner"></span>;

    const buttonClassName = classNames({
      "kuika-button": true,
      "kuika-button__primary": type === "primary",
      "kuika-button__secondary": type === "secondary",
      "kuika-button__tertiary": type === "tertiary",
      "kuika-button__text": type === "text",
      "kuika-button__sm": size === "sm",
      "kuika-button__md": size === "md",
      "kuika-button__lg": size === "lg",
      "kuika-button__uppercase": uppercase,
      "kuika-button__disabled": disabled,
      "kuika-button__loading": isLegacyLoading,
      "kuika-button--status": hasStatus,
      "is-denied": isDenied,
      "kuika-button__neutral": color === "neutral",
      "kuika-button__danger": color === "danger",
      "kuika-button__icon": isIcon,
      "kuika-button__tinted": tinted,
      "kuika-button__active": active,
      "kuika-button__with-icon-button": withIconButton,
      "kuika-button__ai-button": isAIButton,
      [className || ""]: className
    });

    return (
      <button
        ref={ref}
        className={buttonClassName}
        type={htmlType}
        style={style}
        onClick={handleClick}
        onAnimationEnd={handleAnimationEnd}
        disabled={disabled || isLegacyLoading}
        aria-disabled={ariaDisabled || undefined}
        aria-busy={hasStatus ? isBusy : undefined}
        data-status={status}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        tabIndex={tabIndex}
        id={id}
        {...rest}
      >
        {status === undefined ? (
          <>
            {renderLoadingSpinner()}
            {children}
          </>
        ) : (
          <KuikaButtonStatusContent status={status} labels={statusLabels} iconSize={KUIKA_BUTTON_ICON_SIZES[size]}>
            {children}
          </KuikaButtonStatusContent>
        )}
        {withIconButton}
      </button>
    );
  }
);

export default KuikaButton;
